'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo, useCallback, Suspense } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Index, Configure, useInstantSearch, useSearchBox, SearchBox } from 'react-instantsearch';
import { ALGOLIA_INDEX_NAME, SORT_BY_OPTIONS, NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ProductListingPageName from 'components/consumer/ProductListingPage/components/ProductListingPageName';
import DesktopShowHideFiltersButton from 'components/consumer/ProductListingPage/components/desktop/DesktopShowHideFiltersButton';
import MobileGridColumnToggle from 'components/consumer/ProductListingPage/components/mobile/MobileGridColumnToggle';
import DesktopProductListingSidebarContainer from 'components/consumer/ProductListingPage/components/desktop/DesktopProductListingSidebarContainer';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import AdvancedSearchProductHits from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits';
import DesktopProductListingColorFilter from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/DesktopProductListingColorFilter';
import ProductListingSortBy from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/ProductListingSortBy';
import ProductListingPageWidgets from 'components/consumer/ProductListingPageWidgets';
import ProductListingPageDescriptionShell from 'components/consumer/consumer-search/advanced-search-next/components/ProductListingPageDescriptionUI';
import CategoryAlgoliaMobileFilters from './CategoryAlgoliaMobileFilters';
import useCategoryAlgoliaAnalytics from '../hooks/useCategoryAlgoliaAnalytics';
import CategoryAlgoliaSidebar from './CategoryAlgoliaSidebar';
import CategoryCollectionBackLink from './CategoryCollectionBackLink';
import CategoryAlgoliaSEO from './CategoryAlgoliaSEO';
import CategoryAlgoliaJSONLD from './CategoryAlgoliaJSONLD';
import VirtualSearchBox from './VirtualSearchBox';
import { isCollectionPageSlug } from '../categoryPageSlugUtils';
const CategoryAlgoliaLayout = t0 => {
  const $ = _c(96);
  const {
    slug,
    serverCategoryData,
    initialProducts,
    configureFilters,
    selectedFacets,
    onToggleFacet,
    onClearFacets
  } = t0;
  const {
    currentProductIndex,
    minWidth,
    maxWidth
  } = useAdvancedSearchContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      skipSuspense: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    results,
    status
  } = useInstantSearch(t1);
  const {
    query: parentQuery
  } = useSearchBox();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [enrichedProductsByIndex, setEnrichedProductsByIndex] = useState(t2);
  let t3;
  if ($[2] !== currentProductIndex || $[3] !== enrichedProductsByIndex) {
    t3 = enrichedProductsByIndex[currentProductIndex] ?? [];
    $[2] = currentProductIndex;
    $[3] = enrichedProductsByIndex;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const enrichedProducts = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsSidebarOpen(_temp);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const toggleSidebar = t4;
  let t5;
  if ($[6] !== currentProductIndex) {
    t5 = products => {
      setEnrichedProductsByIndex(prev_0 => ({
        ...prev_0,
        [currentProductIndex]: products
      }));
    };
    $[6] = currentProductIndex;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleProductsChange = t5;
  const categoryData = serverCategoryData?.getCategoryPage;
  const pageName = categoryData?.pageName || slug;
  const t6 = categoryData?.pageId;
  let t7;
  if ($[8] !== enrichedProducts || $[9] !== pageName || $[10] !== t6) {
    t7 = {
      pageId: t6,
      pageName,
      products: enrichedProducts
    };
    $[8] = enrichedProducts;
    $[9] = pageName;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  useCategoryAlgoliaAnalytics(t7);
  const productsCount = results?.nbHits || 0;
  const hasProductsLoaded = status === "idle" || status === "error";
  const resultsCount = hasProductsLoaded && productsCount > 0 ? productsCount : null;
  const resultsCountText = resultsCount != null ? `${resultsCount} Products` : null;
  let filters;
  if ($[12] !== maxWidth || $[13] !== minWidth) {
    filters = ["item_availability=0", "visibility>1", "visibility<4"];
    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);
    if (!Number.isNaN(minNum) && minNum >= 1) {
      filters.push(`item_width>=${minNum}`);
    }
    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filters.push(`item_width<=${maxNum}`);
    }
    $[12] = maxWidth;
    $[13] = minWidth;
    $[14] = filters;
  } else {
    filters = $[14];
  }
  const secondaryNumericFilters = filters;
  const isProductsIndex = currentProductIndex === ALGOLIA_INDEX_NAME.PRODUCTS;
  let t8;
  if ($[15] !== categoryData) {
    t8 = <CategoryAlgoliaSEO categoryData={categoryData} />;
    $[15] = categoryData;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = categoryData?.pageLongName || pageName;
  const t10 = categoryData?.pageLongDescription || "";
  const t11 = categoryData?.pageId;
  const t12 = results?.nbHits;
  let t13;
  if ($[17] !== currentProductIndex || $[18] !== enrichedProducts || $[19] !== hasProductsLoaded || $[20] !== t10 || $[21] !== t11 || $[22] !== t12 || $[23] !== t9) {
    t13 = <CategoryAlgoliaJSONLD products={enrichedProducts} pageName={t9} pageDescription={t10} pageId={t11} totalHits={t12} currentProductIndex={currentProductIndex} hasProductsLoaded={hasProductsLoaded} />;
    $[17] = currentProductIndex;
    $[18] = enrichedProducts;
    $[19] = hasProductsLoaded;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = classNames("px-3 py-4 space-y-1", "md:px-0 md:py-0 md:space-y-0", "md:items-center md:bg-white md:flex md:h-[53px] md:sticky md:z-[11] md:gap-5 md:justify-between", "md:top-[117px]");
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== pageName) {
    t15 = <ProductListingPageName pageName={pageName} showPageNameWhileLoading={true} />;
    $[26] = pageName;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== resultsCountText) {
    t16 = resultsCountText && <span className="text-sm text-gray-light2 font-normal">{resultsCountText}</span>;
    $[28] = resultsCountText;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <DesktopShowHideFiltersButton onClick={toggleSidebar} />;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t15 || $[32] !== t16) {
    t18 = <div className="md:flex md:gap-5 md:items-center">{t15}{t16}{t17}</div>;
    $[31] = t15;
    $[32] = t16;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  let t20;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = classNames("flex flex-row justify-between gap-3 py-3", "md:block md:gap-0 md:py-0");
    t20 = classNames("flex-1 flex gap-1", "md:flex-initial md:block md:gap-0");
    $[34] = t19;
    $[35] = t20;
  } else {
    t19 = $[34];
    t20 = $[35];
  }
  const t21 = categoryData?.filters;
  let t22;
  if ($[36] !== onToggleFacet || $[37] !== productsCount || $[38] !== selectedFacets || $[39] !== t21) {
    t22 = <CategoryAlgoliaMobileFilters filters={t21} selectedFacets={selectedFacets} onToggleFacet={onToggleFacet} productsCount={productsCount} />;
    $[36] = onToggleFacet;
    $[37] = productsCount;
    $[38] = selectedFacets;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="flex-1 md:flex-initial md:flex md:flex-row md:gap-4 md:items-center"><DesktopProductListingColorFilter /><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS} /></div>;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t22) {
    t24 = <div className={t20}>{t22}{t23}</div>;
    $[42] = t22;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <MobileGridColumnToggle />;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== t24) {
    t26 = <div className={t19}>{t24}{t25}</div>;
    $[45] = t24;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== t18 || $[48] !== t26) {
    t27 = <div className={t14}>{t18}{t26}</div>;
    $[47] = t18;
    $[48] = t26;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== slug) {
    t28 = isCollectionPageSlug(slug) && <CategoryCollectionBackLink />;
    $[50] = slug;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  const t29 = categoryData?.filters;
  let t30;
  if ($[52] !== onClearFacets || $[53] !== onToggleFacet || $[54] !== pageName || $[55] !== selectedFacets || $[56] !== t29) {
    t30 = <CategoryAlgoliaSidebar pageName={pageName} filters={t29} selectedFacets={selectedFacets} onToggleFacet={onToggleFacet} onClearFacets={onClearFacets} />;
    $[52] = onClearFacets;
    $[53] = onToggleFacet;
    $[54] = pageName;
    $[55] = selectedFacets;
    $[56] = t29;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== isSidebarOpen || $[59] !== t28 || $[60] !== t30) {
    t31 = <div className="hidden md:block"><DesktopProductListingSidebarContainer isOpen={isSidebarOpen}>{t28}{t30}</DesktopProductListingSidebarContainer></div>;
    $[58] = isSidebarOpen;
    $[59] = t28;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== currentProductIndex || $[63] !== enrichedProducts || $[64] !== handleProductsChange || $[65] !== initialProducts || $[66] !== isProductsIndex || $[67] !== serverCategoryData?.getCategoryPage) {
    t32 = isProductsIndex && <AdvancedSearchProductHits key={currentProductIndex} onProductsChange={handleProductsChange} initialProducts={enrichedProducts.length > 0 ? undefined : initialProducts} seedServerData={enrichedProducts.length > 0 ? undefined : serverCategoryData?.getCategoryPage} eagerFirstPageImages={true} enableShowAllButton={true} />;
    $[62] = currentProductIndex;
    $[63] = enrichedProducts;
    $[64] = handleProductsChange;
    $[65] = initialProducts;
    $[66] = isProductsIndex;
    $[67] = serverCategoryData?.getCategoryPage;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== configureFilters || $[70] !== currentProductIndex || $[71] !== handleProductsChange || $[72] !== isProductsIndex || $[73] !== parentQuery || $[74] !== secondaryNumericFilters) {
    t33 = !isProductsIndex && <Index indexName={currentProductIndex} key={currentProductIndex}><SearchBox classNames={{
        root: "hidden"
      }} /><VirtualSearchBox parentQuery={parentQuery} /><Configure facets={["*"]} filters={configureFilters} numericFilters={secondaryNumericFilters} hitsPerPage={NUMBER_OF_PRODUCTS_TO_SHOW} attributesToRetrieve={["_highlightResult", "product_id", "name", "is_in_stock"]} clickAnalytics={true} /><AdvancedSearchProductHits key={currentProductIndex} onProductsChange={handleProductsChange} enableShowAllButton={true} /></Index>;
    $[69] = configureFilters;
    $[70] = currentProductIndex;
    $[71] = handleProductsChange;
    $[72] = isProductsIndex;
    $[73] = parentQuery;
    $[74] = secondaryNumericFilters;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  const t34 = categoryData?.pageLongDescription || "";
  let t35;
  if ($[76] !== pageName || $[77] !== t34) {
    t35 = <ProductListingPageDescriptionShell pageLongDescription={t34} pageName={pageName} />;
    $[76] = pageName;
    $[77] = t34;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== t32 || $[80] !== t33 || $[81] !== t35) {
    t36 = <div className="md:flex md:flex-col md:gap-8 md:mb-8 md:flex-1">{t32}{t33}{t35}</div>;
    $[79] = t32;
    $[80] = t33;
    $[81] = t35;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  let t37;
  if ($[83] !== t31 || $[84] !== t36) {
    t37 = <div className="md:flex md:flex-row md:gap md:mt-2">{t31}{t36}</div>;
    $[83] = t31;
    $[84] = t36;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  const t38 = categoryData?.pageId;
  let t39;
  if ($[86] !== t38) {
    t39 = <Suspense fallback={null}><ProductListingPageWidgets categoryId={t38} /></Suspense>;
    $[86] = t38;
    $[87] = t39;
  } else {
    t39 = $[87];
  }
  let t40;
  if ($[88] !== t27 || $[89] !== t37 || $[90] !== t39) {
    t40 = <FixedWidthContainer wide={true} noMobilePadding={true}>{t27}{t37}{t39}</FixedWidthContainer>;
    $[88] = t27;
    $[89] = t37;
    $[90] = t39;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  let t41;
  if ($[92] !== t13 || $[93] !== t40 || $[94] !== t8) {
    t41 = <>{t8}{t13}{t40}</>;
    $[92] = t13;
    $[93] = t40;
    $[94] = t8;
    $[95] = t41;
  } else {
    t41 = $[95];
  }
  return t41;
};
CategoryAlgoliaLayout.propTypes = {
  slug: PropTypes.string.isRequired,
  serverCategoryData: PropTypes.object,
  initialProducts: PropTypes.array,
  configureFilters: PropTypes.string,
  selectedFacets: PropTypes.object,
  onToggleFacet: PropTypes.func,
  onClearFacets: PropTypes.func
};
export default CategoryAlgoliaLayout;
function _temp(prev) {
  return !prev;
}